import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

export default function Pickup() {
  const [orders, setOrders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorIdx, setSponsorIdx] = useState(0);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const teamFilter = (params.get("team") || params.get("vp") || "").toUpperCase();

  const load = async () => {
    try {
      const [o, t, s] = await Promise.all([
        api.get("/orders?status=klaar&limit=50"),
        api.get("/teams"),
        api.get("/sponsors?only_active=true"),
      ]);
      setOrders(o.data.filter((x) => x.betaal_status !== "wachten"));
      setTeams(t.data);
      setSponsors(s.data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    // eslint-disable-next-line
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, []);

  // Rotate sponsors every 6 seconds
  useEffect(() => {
    if (sponsors.length <= 1) return;
    const id = setInterval(() => {
      setSponsorIdx((i) => (i + 1) % sponsors.length);
    }, 6000);
    return () => clearInterval(id);
  }, [sponsors.length]);

  const activeSponsor = sponsors[sponsorIdx % Math.max(1, sponsors.length)];
  const hasSponsors = sponsors.length > 0;

  const visibleTeams = teamFilter
    ? teams.filter((t) => t.letter === teamFilter)
    : teams;
  const isSingleTeam = visibleTeams.length === 1;

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col" data-testid="pickup-screen">
      <header className="px-6 md:px-10 py-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tight" data-testid="pickup-title">
            {isSingleTeam ? `${visibleTeams[0].naam} · Ophalen` : "Nu Ophalen"}
          </h1>
          <p className="text-[var(--ink-soft)] text-lg mt-1 uppercase tracking-widest">
            {isSingleTeam ? visibleTeams[0].naam : "Klaar voor jou"}
          </p>
        </div>
        <button
          onClick={() => navigate("/")}
          className="btn-press text-sm px-4 py-2 rounded-full bg-white border-2 border-[#ecebe6]"
          data-testid="pickup-exit"
        >
          Sluiten
        </button>
      </header>

      <div className={`flex-1 px-6 md:px-10 ${hasSponsors ? "pb-4" : "pb-10"} grid ${isSingleTeam ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"} gap-4 md:gap-6`}>
        {visibleTeams.map((t) => {
          const list = orders.filter((o) => o.team_id === t.id);
          return (
            <div
              key={t.id}
              className="rounded-[2rem] p-6 md:p-8 flex flex-col text-white overflow-hidden"
              style={{ background: t.kleur }}
              data-testid={`pickup-col-${t.letter}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="font-display text-7xl md:text-8xl font-bold leading-none">{t.letter}</div>
                <div className="flex-1">
                  <div className="text-sm uppercase tracking-widest opacity-80">Team</div>
                  <div className="font-display text-2xl font-bold">{t.naam}</div>
                </div>
              </div>
              <div className="flex-1 flex flex-wrap content-start gap-3 md:gap-4">
                {list.length === 0 ? (
                  <div className="opacity-70 text-lg">Nog niets klaar…</div>
                ) : (
                  list.map((o) => (
                    <div
                      key={o.id}
                      className="bg-white text-[var(--ink)] rounded-3xl px-5 py-3 font-display font-bold text-4xl md:text-5xl shadow-sm"
                      data-testid={`pickup-number-${o.ordernummer}`}
                    >
                      {o.ordernummer}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {hasSponsors && activeSponsor && (
        <div
          className="mx-6 md:mx-10 mb-6 rounded-[2rem] overflow-hidden bg-white border-2 border-[#ecebe6] flex items-center"
          data-testid="sponsor-banner"
          style={{ minHeight: 120 }}
        >
          <div className="px-6 md:px-8 py-3 flex-shrink-0">
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              Mogelijk gemaakt door
            </div>
            <div className="font-display text-2xl md:text-3xl font-bold" data-testid="sponsor-name">
              {activeSponsor.naam}
            </div>
            {sponsors.length > 1 && (
              <div className="flex gap-1 mt-2" data-testid="sponsor-dots">
                {sponsors.map((_, i) => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full transition-all"
                    style={{ background: i === sponsorIdx ? "var(--ink)" : "var(--neutral)" }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="flex-1 h-full flex items-center justify-end pr-4">
            {activeSponsor.afbeelding_url ? (
              <img
                src={activeSponsor.afbeelding_url}
                alt={activeSponsor.naam}
                className="max-h-24 md:max-h-28 object-contain"
                data-testid="sponsor-image"
              />
            ) : (
              <div className="text-[var(--ink-soft)] italic mr-6">Geen afbeelding</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
